import { getHttpClient } from '@/services/httpClientProvider'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'

export interface User {
  id: string
  object: string
  object_id: string
  role_id: string
  email: string
  first_name: string
  last_name: string
  nickname: string
  role: number
  status: number
  time_zone: string
  sms_number: string
  phone_number: string
  phone_ext: string
  avatar: string
  url_avatar?: string // Additional avatar field that might be present
  notes: string
  login_count: number
  updated: string
  created: string
  last_login: string
  first_login: boolean
  perms: string[]
}

export interface UsersListResponse {
  success: boolean
  users: {
    totalCount: number
    results: User[]
  }
}

export interface ArticleEditorsResponse {
  success: boolean
  users?: {
    totalCount: number
    results: Array<{
      id: string
      first_name?: string
      last_name?: string
      full_name?: string
      public_name?: string
      email?: string
      object_id?: string
      [key: string]: any
    }>
  }
}

export interface ArticleEditorOption {
  val: string
  id: string
  lbl: string
  email: string
  partners_id: string
  url_avatar?: string
}

/** Query params for listingArticleEditors; mirrors admin/v4/users listing actions (page/start/limit/sort). */
export interface FetchArticleEditorsParams {
  page?: number
  start?: number
  limit?: number
  sort?: Array<{ property: string; direction: string }>
}

const defaultArticleEditorsSort = [
  { property: 'first_name', direction: 'ASC' },
  { property: 'last_name', direction: 'ASC' }
] as const satisfies Array<{ property: string; direction: string }>

export interface FetchUserListParams {
  page?: number
  start?: number
  limit?: number
  sort?: Array<{ property: string; direction: string }>
  teamId?: string // Optional team filter
  organizationId?: string // Optional organization filter
}

export interface FetchUserListSelfParams {
  page?: number
  start?: number
  limit?: number
  sort?: Array<{ property: string; direction: string }>
  userId?: string // Optional user ID filter
  organizationId?: string // Optional organization filter
}

export interface DeleteUserParams {
  userId: string
}

export interface DeleteUserResponse {
  success: boolean
  message?: string
}

export interface UpdateUserParams {
  id: string
  first_name?: string
  last_name?: string
  email?: string
  phone_number?: string
  phone_ext?: string
  sms_number?: string
  nickname?: string
  time_zone?: string
  role_id?: string
  _teams_ids?: string[]
  status?: number
  password_action?: 'lock' | 'unlock' | 'resetlink' | 'setpass'
  newpassword?: string
  // Password change fields
  old_password?: string
  password?: string
  confirm_password?: string
  country_id?: number | string
}

export interface UpdateUserResponse {
  success: boolean
  users: {
    totalCount: number
    results: User[]
  }
  message?: string
}

export interface CreateUserParams {
  first_name: string
  last_name: string
  email: string
  role_id?: string
}

export interface CreateUserResponse {
  success: boolean
  users: {
    totalCount: number
    results: User[]
  }
  message?: string
}

export function useUsersAPI() {
  const authStore = useAuthStore()
  const httpClient = getHttpClient(authStore)

  async function fetchUserList(params: FetchUserListParams = {}): Promise<UsersListResponse> {
    // Get user context for organization filtering
    const userStore = useUserStore()
    const currentUserOrgId = userStore.userData?.context_org_id || userStore.userData?.object_id

    const {
      page = 1,
      start = 0,
      limit = 25,
      sort = [{ property: 'email', direction: 'ASC' }],
      teamId,
      organizationId
    } = params

    // Build filter array with automatic organization context filtering
    const filters = [
      { property: 'id', value: '_no_filter_' },
      { property: 'object', value: 'partners' },
      { property: 'role_id', value: '_no_filter_' },
      { property: 'partners_id', value: currentUserOrgId || '_no_filter_' },
      { property: 'context_org_id', value: currentUserOrgId || '_no_filter_' }
    ]

    // Add organization filter if provided (this will override the automatic one)
    if (organizationId) {
      filters.push({ property: 'object_id', value: organizationId })
    }

    // Add team filter if provided
    if (teamId) {
      filters.push({ property: 'project_partner_users', value: teamId })
    }

    const response = await httpClient.get<UsersListResponse>('admin/v4/users/', {
      sAction: 'listing',
      page: page.toString(),
      start: start.toString(),
      limit: limit.toString(),
      sort: JSON.stringify(sort),
      filter: JSON.stringify(filters)
    })
    
    if (!response.success) {
      throw new Error('Failed to fetch users')
    }

    return response
  }

  async function fetchArticleEditors(
    params: FetchArticleEditorsParams = {}
  ): Promise<ArticleEditorOption[]> {
    const {
      page = 1,
      start = 0,
      limit = 500,
      sort = [...defaultArticleEditorsSort]
    } = params

    const response = await httpClient.get<ArticleEditorsResponse>('admin/v4/users/', {
      sAction: 'listingArticleEditors',
      page: page.toString(),
      start: start.toString(),
      limit: limit.toString(),
      sort: JSON.stringify(sort)
    })

    if (!response.success) {
      throw new Error('Failed to fetch article editors')
    }

    return (response.users?.results || []).map((user) => ({
      val: user.id,
      id: user.id,
      lbl: user.full_name || user.public_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || user.id,
      email: user.email || '',
      partners_id: user.object_id || '',
      url_avatar: user.url_avatar || user.avatar || undefined
    }))
  }

  async function fetchUserListSelf(params: FetchUserListSelfParams = {}): Promise<UsersListResponse> {
    const {
      page = 1,
      start = 0,
      limit = 25,
      sort = [{ property: 'email', direction: 'ASC' }],
      userId,
      organizationId
    } = params

    // Build filter array
    const filters = [
      { property: 'id', value: userId || '_no_filter_' },
      { property: 'object', value: 'partners' },
      { property: 'object_id', value: organizationId || '_no_filter_' },
      { property: 'project_partner_users', value: '_no_filter_' },
      { property: 'role_id', value: '_no_filter_' }
    ]

    const response = await httpClient.get<UsersListResponse>('admin/v4/users/', {
      sAction: 'listingSelf',
      page: page.toString(),
      start: start.toString(),
      limit: limit.toString(),
      sort: JSON.stringify(sort),
      filter: JSON.stringify(filters)
    })
    
    if (!response.success) {
      throw new Error('Failed to fetch users with listingSelf')
    }

    return response
  }

  async function deleteUser(userId: string): Promise<DeleteUserResponse> {
    // Body parameters matching the fetch call structure
    const payload = new URLSearchParams()
    payload.append('users', JSON.stringify([{ id: userId }]))

    const response = await httpClient.post<DeleteUserResponse>(
      'admin/v4/users/',
      payload,
      { sAction: 'delete' },
      { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' }
    )
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete user')
    }

    return response
  }

  async function updateUser(params: UpdateUserParams): Promise<UpdateUserResponse> {
    // Build the user object with only the fields that are provided
    const userUpdate: Record<string, any> = {
      id: params.id
    }

    // Add optional fields if provided
    if (params.first_name !== undefined) userUpdate.first_name = params.first_name
    if (params.last_name !== undefined) userUpdate.last_name = params.last_name
    if (params.email !== undefined) userUpdate.email = params.email
    if (params.phone_number !== undefined) userUpdate.phone_number = params.phone_number
    if (params.phone_ext !== undefined) userUpdate.phone_ext = params.phone_ext
    if (params.sms_number !== undefined) userUpdate.sms_number = params.sms_number
    if (params.nickname !== undefined) userUpdate.nickname = params.nickname
    if (params.time_zone !== undefined) userUpdate.time_zone = params.time_zone
    if (params.status !== undefined) userUpdate.status = params.status
    if (params.role_id !== undefined) userUpdate.role_id = params.role_id
    if (params._teams_ids !== undefined) userUpdate._teams_ids = params._teams_ids
    if (params.password_action !== undefined) userUpdate.password_action = params.password_action
    if (params.newpassword !== undefined) userUpdate.newpassword = params.newpassword
    if (params.old_password !== undefined) userUpdate.old_password = params.old_password
    if (params.password !== undefined) userUpdate.password = params.password
    if (params.confirm_password !== undefined) userUpdate.confirm_password = params.confirm_password
    if (params.country_id !== undefined) userUpdate.country_id = params.country_id

    // Body parameters matching the fetch call structure
    const payload = new URLSearchParams()
    payload.append('users', JSON.stringify([userUpdate]))

    try {
      const response = await httpClient.post<UpdateUserResponse>(
        'admin/v4/users/',
        payload,
        { sAction: 'put' },
        { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' }
      )
      
      if (!response.success) {
        // Create an error object that includes the fields array for better error handling
        const error: any = new Error(response.message || 'Failed to update user')
        error.fields = (response as any).fields || []
        error.errorType = (response as any).errorType
        throw error
      }

      return response
    } catch (error: any) {
      // If httpClient threw an error (e.g., 400 status), extract the response data
      if (error.response) {
        const enhancedError: any = new Error(error.response.message || 'Failed to update user')
        enhancedError.fields = error.response.fields || []
        enhancedError.errorType = error.response.errorType
        enhancedError.status = error.status
        throw enhancedError
      }
      throw error
    }
  }

  async function createUser(params: CreateUserParams): Promise<CreateUserResponse> {
    // Get user context for organization
    const userStore = useUserStore()
    const partnerId = userStore.userData?.context_org_id || userStore.userData?.object_id

    if (!partnerId) {
      throw new Error('Partner ID not found')
    }

    // Generate a temporary ID for the new user (matches the pattern from the working example)
    // Using crypto.randomUUID() instead of Math.random() for better uniqueness and security
    const tempId = `boomtown.model.config.Users-${Date.now()}-${crypto.randomUUID().slice(0, 8)}`

    const userPayload: any = {
      id: tempId, // Temporary ID signals to API this is a new user
      object: 'partners',
      object_id: partnerId,
      first_name: params.first_name,
      last_name: params.last_name,
      email: params.email,
      role_id: params.role_id || '', // Empty string if not provided
      status: 1 // Active by default
    }

    // Body parameters matching the fetch call structure
    const payload = new URLSearchParams()
    payload.append('users', JSON.stringify([userPayload]))

    const response = await httpClient.post<CreateUserResponse>(
      'admin/v4/users/',
      payload,
      { sAction: 'put' },
      { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' }
    )

    if (!response.success) {
      throw new Error(response.message || 'Failed to create user')
    }

    return response
  }

  async function cloneUser(userPayload: any): Promise<CreateUserResponse> {
    // Body parameters for clone operation
    const payload = new URLSearchParams()
    payload.append('users', JSON.stringify([userPayload]))

    const response = await httpClient.post<CreateUserResponse>(
      'admin/v4/users/',
      payload,
      { sAction: 'put' },
      { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' }
    )

    if (!response.success) {
      throw new Error(response.message || 'Failed to clone user')
    }

    return response
  }

  return {
    fetchUserList,
    fetchArticleEditors,
    fetchUserListSelf,
    deleteUser,
    updateUser,
    createUser,
    cloneUser
  }
} 